/**
 * FindSchools.js
 *
 * The unauthenticated "find your school" experience. Lets a prospective user
 * search for their school or organization and shows a status card indicating
 * whether it is already part of the CommunityShare network. Assembles the
 * signup banner, search, status card, a contextual call to action, and the
 * network logos.
 */
import { LinkButton } from '@communityshare/ui-shared';
import ScrollToTop from '@/components/ScrollToTop';
import ActionButton from '@/components/ui/ActionButton';
import NetworkLogos from '@/components/ui/NetworkLogos';
import RemoveLoadingScreen from '@/pages/RemoveLoadingScreen';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import SchoolSearchField from './SchoolSearchField';
import SchoolStatusCard, { getSchoolStatus, SchoolStatus } from './SchoolStatusCard';
import SignupBanner from './SignupBanner';

const SIGNUP_HREF = '/signup';
const CONTACT_HREF = 'https://www.communityshare.org/contact-us/';

/**
 * The find-schools page content.
 *
 * @returns {JSX.Element} The rendered page.
 */
export default function FindSchools() {
  const { t } = useTranslation();
  const [siteData, setSiteData] = useState(null);

  const status = getSchoolStatus(siteData);
  const isActive = status === SchoolStatus.ACTIVE;

  /**
   * Resets the selection when the user edits the search input.
   *
   * @param {string} value - The new input value.
   */
  function onInputChange(value) {
    if (!value) setSiteData(null);
    else if (siteData) setSiteData(null);
  }

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <Helmet>
        <title>{t('find_schools.title')}</title>
      </Helmet>
      <ScrollToTop/>
      <RemoveLoadingScreen/>

      <SignupBanner
        headline={t('find_schools.banner_headline')}
        body={t('find_schools.banner_body')}
      />

      <div className="w-full max-w-screen-xl mx-auto px-5 md:px-8 lg:px-16 py-8 flex-1 flex flex-col gap-10">
        <div className="mt-2">
          <LinkButton
            label={t('find_schools.back_to_signup')}
            icon="left"
            href={SIGNUP_HREF}
          />
        </div>

        {/* site search */}
        <div className="flex flex-col gap-6 max-w-xl">
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl font-semibold text-cs-gray-900">
              {t('find_schools.title')}
            </h1>
            <p className="text-base text-cs-gray-500">
              {t('find_schools.subtitle')}
            </p>
          </div>

          <SchoolSearchField
            value={siteData?.name || ''}
            onSelect={setSiteData}
            onInputChange={onInputChange}
            hideDetails={Boolean(status)}
          />

          {status && <SchoolStatusCard siteData={siteData}/>}

          {status && (
            <div className="flex flex-col gap-2">
              {isActive ? (
                <ActionButton href={SIGNUP_HREF}>
                  {t('find_schools.continue_signup')}
                </ActionButton>
              ) : (
                <>
                  <ActionButton href={CONTACT_HREF}>
                    {t('find_schools.contact_us')}
                  </ActionButton>
                  <p className="text-sm text-cs-gray-500">
                    {t('find_schools.contact_us_subtext')}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <NetworkLogos title={t('find_schools.network_logos_title')}/>

      </div>
    </div>
  );
}
